/**
 * Rodzina PRODUCTION — etykiety produkcyjne Fabrica (pierwszy wołający organu
 * druku; ślad partii W12).
 *
 * Zestawy zmiennych są KONTRAKTEM z panelem produkcyjnym — wyspecyfikowane
 * w `docs/FABRICA_PANEL/koncepcje/arch-3-wspolpraca-organow.md:1180-1196`
 * (tabela „Kiedy panel drukuje — i skąd bierze treść"). Nazwy zmiennych nie
 * mogą się zmienić bez podbicia `version` i uzgodnienia z kurierem panelu.
 *
 * ALERGENY: wyłącznie z listy potwierdzonej przez technologa — zmienna, nigdy
 * logika w szablonie. Receptor niesie jeden bit `IsAllergen` na `Stock`,
 * a wyprowadzenie per-składnikowych alergenów z wolnotekstowego `Composition`
 * MUSI przejść przez technologa. Wzór drukuje dokładnie to, co dostał;
 * panel drukujący zgadnięty alergen to zdarzenie bezpieczeństwa żywności.
 */

import { kompozycja, zawijaj, type PrintNode } from '../ir';
import { list, s, type PrintTemplateDef, type PrintVars } from '../types';

/** Wiersze zawiniętego tekstu jako węzły (wspólne łamanie dla wszystkich celów). */
function tekstWielolinijkowy(
  xMm: number,
  yMm: number,
  text: string,
  opts: { sizePt?: number; bold?: boolean; maxChars: number; stepMm: number; maxLines?: number }
): PrintNode {
  const maxLines = opts.maxLines ?? 3;
  return {
    kind: 'repeat',
    yMm,
    stepMm: opts.stepMm,
    rows: zawijaj(text, opts.maxChars, maxLines).map((linia) => [
      { kind: 'text', xMm, yMm: 0, text: linia, sizePt: opts.sizePt, bold: opts.bold },
    ]),
    // Reguła łamania obok jej wyniku — bez tego zasiew edytora musiałby ją
    // zgadywać z `rows`, a zgadywał zawsze ciaśniej niż projekt.
    zawijanie: { maxChars: opts.maxChars, maxLines },
  };
}

/** Lista alergenów do druku — pusta ZATWIERDZONA lista znaczy „brak". */
function alergenyTekst(v: PrintVars): string {
  const pozycje = list<string>(v, 'allergens').map((a) => String(a));
  return pozycje.length > 0 ? `ALERGENY: ${pozycje.join(', ')}` : 'ALERGENY: brak';
}

export const productionTemplates: PrintTemplateDef[] = [
  // ---------------------------------------------------------------------------
  {
    slug: 'fabrica.batch_label',
    name: 'Etykieta partii',
    family: 'production',
    organ: 'Fabrica',
    trigger: 'Zamknięcie partii w panelu produkcyjnym (fabrica-panel → proxy T11 w fabrica-core).',
    media: { kind: 'label', widthMm: 100, heightMm: 60, dpi: 203 },
    targets: ['ZPL', 'EPL', 'TSPL', 'PDF'],
    version: 1,
    variables: [
      {
        name: 'batch_number',
        description: 'Numer partii nadany przez fabrica-core.',
        example: 'PB-2026-0824-03',
      },
      {
        name: 'recipe_name',
        description: 'Nazwa receptury / produktu.',
        example: 'Chleb żytni na zakwasie 700 g',
      },
      {
        name: 'net_weight',
        description: 'Masa netto z jednostką.',
        example: '700 g',
      },
      {
        name: 'produced_at',
        description: 'Data i godzina wyprodukowania.',
        example: '24.08.2026, 06:40',
        kind: 'date',
      },
      {
        name: 'best_before',
        description: 'Najlepiej spożyć przed — policzone z `fabrica_recipes.shelf_life_hours`.',
        example: '27.08.2026',
        kind: 'date',
      },
      {
        name: 'allergens',
        description:
          'Lista alergenów ZATWIERDZONA przez technologa — wzór drukuje ją dosłownie, niczego nie wnioskuje.',
        example: ['gluten (żyto)', 'może zawierać: sezam'],
        kind: 'list',
      },
      {
        name: 'operator_initials',
        description: 'Inicjały operatora zamykającego partię.',
        example: 'KW',
      },
      {
        name: 'batch_barcode',
        description: 'Kod kreskowy partii (Code 128) — skanowany na wózkach i przy wydaniu.',
        example: 'PB-2026-0824-03',
        kind: 'barcode',
      },
    ],
    slad: { keys: ['batch_id'] },
    build: (v) =>
      kompozycja({ kind: 'label', widthMm: 100, heightMm: 60, dpi: 203 }, [
        { kind: 'box', xMm: 1, yMm: 1, widthMm: 98, heightMm: 58, thicknessMm: 0.4 },
        { kind: 'text', xMm: 4, yMm: 3, text: 'ETYKIETA PARTII', sizePt: 6 },
        {
          kind: 'text',
          xMm: 78,
          yMm: 3,
          text: `Operator: ${s(v, 'operator_initials')}`,
          sizePt: 6,
        },
        tekstWielolinijkowy(4, 7, s(v, 'recipe_name'), {
          sizePt: 12,
          bold: true,
          maxChars: 26,
          stepMm: 6,
          maxLines: 2,
        }),
        { kind: 'line', xMm: 4, yMm: 20, x2Mm: 96, y2Mm: 20, thicknessMm: 0.4 },
        {
          kind: 'text',
          xMm: 4,
          yMm: 22,
          text: `Partia: ${s(v, 'batch_number')}`,
          sizePt: 11,
          bold: true,
        },
        { kind: 'text', xMm: 4, yMm: 29, text: `Wyprodukowano: ${s(v, 'produced_at')}`, sizePt: 8 },
        {
          kind: 'text',
          xMm: 4,
          yMm: 34,
          text: `Najlepiej spożyć przed: ${s(v, 'best_before')}`,
          sizePt: 9,
          bold: true,
        },
        { kind: 'text', xMm: 4, yMm: 40, text: `Masa netto: ${s(v, 'net_weight')}`, sizePt: 8 },
        tekstWielolinijkowy(4, 45, alergenyTekst(v), {
          sizePt: 8,
          bold: true,
          maxChars: 38,
          stepMm: 4,
          maxLines: 3,
        }),
        {
          kind: 'barcode',
          xMm: 58,
          yMm: 24,
          data: s(v, 'batch_barcode'),
          symbology: 'code128',
          heightMm: 12,
          showText: true,
        },
      ]),
  },

  // ---------------------------------------------------------------------------
  {
    slug: 'fabrica.wip_label',
    name: 'Etykieta półproduktu (WIP)',
    family: 'production',
    organ: 'Fabrica',
    trigger: 'Odstawienie półproduktu na wózek między etapami produkcji.',
    media: { kind: 'label', widthMm: 80, heightMm: 50, dpi: 203 },
    targets: ['ZPL', 'EPL', 'TSPL', 'PDF'],
    version: 1,
    variables: [
      {
        name: 'batch_number',
        description: 'Numer partii, do której należy półprodukt.',
        example: 'PB-2026-0824-03',
      },
      {
        name: 'stage_name',
        description: 'Etap produkcji, na którym stoi wózek.',
        example: 'Wyrastanie — garownia',
      },
      {
        name: 'quantity',
        description: 'Ilość na wózku z jednostką.',
        example: '64 szt.',
      },
      {
        name: 'started_at',
        description: 'Początek etapu.',
        example: '24.08.2026, 08:15',
        kind: 'date',
      },
      {
        name: 'workstation',
        description: 'Stanowisko, z którego wyjechał wózek.',
        example: 'Stanowisko W-2',
      },
    ],
    slad: { keys: ['batch_id', 'production_stage_id'] },
    build: (v) =>
      kompozycja({ kind: 'label', widthMm: 80, heightMm: 50, dpi: 203 }, [
        { kind: 'box', xMm: 1, yMm: 1, widthMm: 78, heightMm: 48, thicknessMm: 0.4 },
        { kind: 'text', xMm: 4, yMm: 3, text: 'PÓŁPRODUKT', sizePt: 6 },
        tekstWielolinijkowy(4, 7, s(v, 'stage_name'), {
          sizePt: 12,
          bold: true,
          maxChars: 20,
          stepMm: 6,
          maxLines: 2,
        }),
        { kind: 'line', xMm: 4, yMm: 19, x2Mm: 76, y2Mm: 19, thicknessMm: 0.4 },
        {
          kind: 'text',
          xMm: 4,
          yMm: 21,
          text: `Partia: ${s(v, 'batch_number')}`,
          sizePt: 9,
          bold: true,
        },
        { kind: 'text', xMm: 4, yMm: 27, text: `Ilość: ${s(v, 'quantity')}`, sizePt: 9 },
        { kind: 'text', xMm: 4, yMm: 33, text: `Start: ${s(v, 'started_at')}`, sizePt: 8 },
        { kind: 'text', xMm: 4, yMm: 38, text: s(v, 'workstation'), sizePt: 8 },
        // QR z numerem partii — skan na kolejnym stanowisku wiąże wózek z partią.
        { kind: 'qr', xMm: 56, yMm: 24, data: s(v, 'batch_number'), sizeMm: 20 },
      ]),
  },

  // ---------------------------------------------------------------------------
  {
    slug: 'fabrica.weighed_container',
    name: 'Etykieta pojemnika po naważeniu',
    family: 'production',
    organ: 'Fabrica',
    trigger: 'Naważenie składnika do pojemnika z przeznaczeniem na później.',
    media: { kind: 'label', widthMm: 80, heightMm: 50, dpi: 203 },
    targets: ['ZPL', 'EPL', 'TSPL', 'PDF'],
    version: 1,
    variables: [
      {
        name: 'ingredient_name',
        description: 'Nazwa składnika.',
        example: 'Mąka pszenna typ 750',
      },
      {
        name: 'actual_quantity',
        description: 'Rzeczywista naważona ilość z jednostką (z wagi SWS).',
        example: '12,48 kg',
      },
      {
        name: 'supplier_batch_number',
        description: 'Numer partii dostawcy — wpis/skan operatora przy naważaniu.',
        example: 'ML-750/2026/081',
      },
      {
        name: 'weighed_at',
        description: 'Data i godzina naważenia.',
        example: '24.08.2026, 05:52',
        kind: 'date',
      },
      {
        name: 'worker',
        description: 'Pracownik, który naważył.',
        example: 'Marek Zieliński',
      },
    ],
    slad: { keys: ['production_order_line_id'] },
    build: (v) =>
      kompozycja({ kind: 'label', widthMm: 80, heightMm: 50, dpi: 203 }, [
        { kind: 'box', xMm: 1, yMm: 1, widthMm: 78, heightMm: 48, thicknessMm: 0.4 },
        { kind: 'text', xMm: 4, yMm: 3, text: 'NAWAŻONO', sizePt: 6 },
        tekstWielolinijkowy(4, 7, s(v, 'ingredient_name'), {
          sizePt: 12,
          bold: true,
          maxChars: 20,
          stepMm: 6,
          maxLines: 2,
        }),
        {
          kind: 'text',
          xMm: 4,
          yMm: 20,
          text: s(v, 'actual_quantity'),
          sizePt: 18,
          bold: true,
        },
        { kind: 'line', xMm: 4, yMm: 31, x2Mm: 76, y2Mm: 31, thicknessMm: 0.4 },
        {
          kind: 'text',
          xMm: 4,
          yMm: 33,
          text: `Partia dostawcy: ${s(v, 'supplier_batch_number')}`,
          sizePt: 8,
        },
        { kind: 'text', xMm: 4, yMm: 38, text: `Naważono: ${s(v, 'weighed_at')}`, sizePt: 8 },
        { kind: 'text', xMm: 4, yMm: 43, text: `Pracownik: ${s(v, 'worker')}`, sizePt: 8 },
      ]),
  },

  // ---------------------------------------------------------------------------
  {
    slug: 'fabrica.waste_label',
    name: 'Przelepka odpadu',
    family: 'production',
    organ: 'Fabrica',
    trigger: 'Zarejestrowanie odpadu produkcyjnego w panelu (przelepka na pojemnik).',
    media: { kind: 'label', widthMm: 80, heightMm: 50, dpi: 203 },
    targets: ['ZPL', 'EPL', 'TSPL', 'PDF'],
    version: 1,
    variables: [
      {
        name: 'waste_kg',
        description: 'Masa odpadu w kilogramach.',
        example: 3.2,
        kind: 'number',
      },
      {
        name: 'waste_type',
        description: 'Rodzaj odpadu.',
        example: 'zwrot z wypieku — przypalone',
      },
      {
        name: 'reason',
        description: 'Powód powstania odpadu.',
        example: 'Awaria pieca P-2, strefa 3 przegrzana',
      },
      {
        name: 'order_number',
        description: 'Numer zlecenia produkcyjnego, z którego pochodzi odpad.',
        example: 'ZP-2026-0824-11',
      },
    ],
    slad: { keys: ['production_order_line_id'] },
    build: (v) =>
      kompozycja({ kind: 'label', widthMm: 80, heightMm: 50, dpi: 203 }, [
        { kind: 'box', xMm: 1, yMm: 1, widthMm: 78, heightMm: 48, thicknessMm: 0.8 },
        {
          kind: 'text',
          xMm: 4,
          yMm: 4,
          text: 'ODPAD',
          sizePt: 20,
          bold: true,
          maxWidthMm: 72,
          align: 'center',
        },
        {
          kind: 'text',
          xMm: 4,
          yMm: 15,
          text: `${s(v, 'waste_kg')} kg`,
          sizePt: 14,
          bold: true,
          maxWidthMm: 72,
          align: 'center',
        },
        { kind: 'line', xMm: 4, yMm: 24, x2Mm: 76, y2Mm: 24, thicknessMm: 0.4 },
        tekstWielolinijkowy(4, 26, s(v, 'waste_type'), {
          sizePt: 9,
          bold: true,
          maxChars: 30,
          stepMm: 4.5,
          maxLines: 2,
        }),
        tekstWielolinijkowy(4, 35, `Powód: ${s(v, 'reason')}`, {
          sizePt: 8,
          maxChars: 36,
          stepMm: 4,
          maxLines: 2,
        }),
        {
          kind: 'text',
          xMm: 4,
          yMm: 44,
          text: `Zlecenie: ${s(v, 'order_number')}`,
          sizePt: 8,
        },
      ]),
  },
];
